import React, { useCallback, useState, useRef, useEffect } from 'react';
import { View, Pressable, Platform, FlatList } from 'react-native';
import { XStack, YStack, Text } from 'tamagui';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface DataSource {
  _id: string;
  dataTitle: string;
  description?: string;
  dataType?: string;
  dataTags?: string[];
}

interface DataSourceCardProps {
  dataSource: DataSource;
  theme: boolean;
  isSelected: boolean;
  onToggle: (dataSourceId: string) => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  dataSource,
  theme,
  isSelected,
  onToggle
}) => {
  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: theme ? '#e5e7eb' : '#4b5563',
    selectedBorder: '#3b82f6',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    selectedBackground: theme ? '#eff6ff' : '#1e3a5f',
  };

  return (
    <Pressable
      onPress={() => onToggle(dataSource._id)}
      style={{
        width: Platform.OS === 'web' ? 280 : 240,
        backgroundColor: isSelected ? cardColors.selectedBackground : cardColors.background,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: isSelected ? cardColors.selectedBorder : cardColors.border,
        padding: 16,
        marginRight: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }}
    >
      <XStack alignItems="center" gap={12} marginBottom={12}>
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 24,
            backgroundColor: theme ? '#f3f4f6' : '#4b5563',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MaterialCommunityIcons
            name="database"
            size={24}
            color={isSelected ? cardColors.accent : (theme ? '#6b7280' : '#9ca3af')}
          />
        </View>
        <YStack flex={1}>
          <Text
            fontSize={16}
            fontWeight="600"
            color={cardColors.text}
            numberOfLines={1}
          >
            {dataSource.dataTitle}
          </Text>
          {dataSource.dataType && (
            <Text fontSize={12} color={cardColors.textSecondary} marginTop={4}>
              {dataSource.dataType}
            </Text>
          )}
        </YStack>
        {isSelected && (
          <MaterialCommunityIcons
            name="check-circle"
            size={20}
            color={cardColors.accent}
          />
        )}
      </XStack>

      {dataSource.description && (
        <Text
          fontSize={12}
          color={cardColors.textSecondary}
          numberOfLines={2}
          marginBottom={12}
        >
          {dataSource.description}
        </Text>
      )}

      {dataSource.dataTags && dataSource.dataTags.length > 0 && (
        <XStack flexWrap="wrap" gap={4} marginTop={8}>
          {dataSource.dataTags.slice(0, 3).map((tag, index) => (
            <View
              key={index}
              style={{
                backgroundColor: theme ? '#f3f4f6' : '#4b5563',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 4,
              }}
            >
              <Text fontSize={10} color={cardColors.textSecondary}>
                {tag}
              </Text>
            </View>
          ))}
        </XStack>
      )}
    </Pressable>
  );
};

interface DataSourceSelectorProps {
  dataSources: DataSource[];
  selectedIds: string[];
  theme: boolean;
  onSelectionChange: (selectedIds: string[]) => void;
  onConfirm: (selectedIds: string[]) => void;
}

export const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  dataSources,
  selectedIds,
  theme,
  onSelectionChange,
  onConfirm
}) => {
  const handleToggle = useCallback((dataSourceId: string) => {
    // Use functional update pattern since onSelectionChange is setSelectedDataSources
    // This prevents dependency on selectedIds which changes on every toggle
    onSelectionChange((prevIds: string[]) => {
      const newSelection = prevIds.includes(dataSourceId)
        ? prevIds.filter(id => id !== dataSourceId)
        : [...prevIds, dataSourceId];
      return newSelection;
    });
  }, [onSelectionChange]);

  // Use useRef to store the latest selectedIds without causing re-renders
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const handleConfirm = useCallback(() => {
    // Read from ref to avoid dependency on selectedIds
    // This prevents the callback from being recreated on every selection change
    const currentIds = selectedIdsRef.current;
    if (currentIds.length > 0) {
      onConfirm(currentIds);
    }
  }, [onConfirm]); // Only depend on onConfirm, which should be stable

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    text: theme ? '#1f2937' : '#f9fafb',
    accent: '#3b82f6',
  };

  return (
    <View style={{ width: '100%' }}>
      <Text
        fontSize={14}
        fontWeight="600"
        color={cardColors.text}
        marginBottom={12}
      >
        Select data sources for your dashboard ({selectedIds.length} selected)
      </Text>
      
      <FlatList
        data={dataSources}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={(item) => item._id}
        renderItem={({ item }) => (
          <DataSourceCard
            dataSource={item}
            theme={theme}
            isSelected={selectedIds.includes(item._id)}
            onToggle={handleToggle}
          />
        )}
        contentContainerStyle={{
          paddingHorizontal: 4,
          paddingVertical: 8,
          paddingBottom: 12,
        }}
      />

      {selectedIds.length > 0 && (
        <Pressable
          onPress={handleConfirm}
          style={{
            marginTop: 16,
            backgroundColor: cardColors.accent,
            paddingVertical: 12,
            paddingHorizontal: 24,
            borderRadius: 8,
            alignItems: 'center',
          }}
        >
          <Text fontSize={14} fontWeight="600" color="white">
            Create Dashboard with {selectedIds.length} {selectedIds.length === 1 ? 'Source' : 'Sources'}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

export default DataSourceSelector;



